function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ProfileContext } from 'context';
import { arraySum, bankersRound } from 'helpers/math';
import { getStandards } from 'helpers/standards';
import App from 'modules/App';
import * as selectors from 'modules/PatientResults/selectors';
import { MAX_VALUE } from '../../../constants';
const MIN_HEIGHT = 5; // in percents
const baseLevelCommon = {
    count: 0,
    percentage: 20,
    height: 20,
    bottomThreshold: 0,
    topThreshold: 0
};
const getBaseLevels = (showTightRange)=>[
        _object_spread({
            name: 'veryHigh'
        }, baseLevelCommon),
        _object_spread({
            name: 'high'
        }, baseLevelCommon),
        _object_spread_props(_object_spread({
            name: 'target'
        }, baseLevelCommon), {
            isInclusive: !showTightRange
        }),
        showTightRange ? _object_spread_props(_object_spread({
            name: 'tight'
        }, baseLevelCommon), {
            isInclusive: true
        }) : null,
        _object_spread_props(_object_spread({
            name: 'low'
        }, baseLevelCommon), {
            isInclusive: true
        }),
        _object_spread_props(_object_spread({
            name: 'veryLow'
        }, baseLevelCommon), {
            isInclusive: true
        })
    ].filter(Boolean);
const getGlucoseLevels = (standards, showTightRange)=>{
    const { highThreshold: veryHigh, lowThreshold: low } = standards.critical;
    const { highThreshold: high, lowThreshold: target } = standards.postMeal;
    const { highThreshold: tight } = standards.preMeal;
    const bottomThresholds = {
        veryHigh,
        high,
        target: showTightRange ? tight : target,
        tight: target,
        low,
        veryLow: 0
    };
    const topThresholds = {
        veryHigh: MAX_VALUE,
        high: veryHigh,
        target: high,
        tight,
        low: target,
        veryLow: low
    };
    return getBaseLevels(showTightRange).map((lvl)=>_object_spread_props(_object_spread({}, lvl), {
            bottomThreshold: bottomThresholds[lvl.name],
            topThreshold: topThresholds[lvl.name]
        }));
};
const calculateGlucoseLevels = (readings, standards, showTightRange)=>{
    const glucoseLevels = getGlucoseLevels(standards, showTightRange);
    if (!readings || readings.length === 0) {
        return glucoseLevels;
    }
    let maxValue = MAX_VALUE;
    readings.forEach((reading)=>{
        if (reading.value > maxValue) {
            maxValue = Math.ceil(reading.value);
        }
        const level = glucoseLevels.find((lvl)=>lvl.isInclusive ? reading.value >= lvl.bottomThreshold : reading.value > lvl.bottomThreshold);
        level.count += 1;
    });
    glucoseLevels[0].topThreshold = maxValue;
    glucoseLevels.forEach((lvl)=>{
        const { count } = lvl;
        lvl.percentage = bankersRound(count / readings.length * 100);
    });
    // Set min height to 5% and compensate real heights
    const heightPercents = glucoseLevels.map((gcl)=>Math.max(gcl.percentage, MIN_HEIGHT));
    let percentSum = arraySum(heightPercents);
    while(percentSum > 100){
        let excess = percentSum - 100;
        const topPercents = heightPercents.filter((hp)=>hp > 10).sort((a, b)=>b - a);
        topPercents.forEach((tp)=>{
            if (!excess) return;
            const idx = heightPercents.findIndex((hp)=>hp === tp);
            heightPercents[idx]--;
            excess--;
        });
        percentSum = arraySum(heightPercents);
    }
    glucoseLevels.forEach((lvl, idx)=>{
        lvl.height = heightPercents[idx];
    });
    return glucoseLevels;
};
export const useGlucoseConcentrationLevels = (readings, isAGP = false)=>{
    const profileContext = useContext(ProfileContext);
    const phiSet = useSelector(profileContext.selectors.phiSet);
    const clinicSettings = useSelector(profileContext.selectors.clinicSettings);
    const countrySettings = useSelector(App.selectors.countrySettings);
    const showTightRange = useSelector(selectors.showTightRange);
    const [glucoseConcentrationLevels, setGlucoseConcentrationLevels] = useState(getBaseLevels(showTightRange));
    useEffect(()=>{
        const standards = getStandards(phiSet, countrySettings, clinicSettings, isAGP);
        const glucoseLevels = calculateGlucoseLevels(readings, standards, showTightRange);
        setGlucoseConcentrationLevels(glucoseLevels);
    }, [
        readings,
        phiSet
    ]);
    return glucoseConcentrationLevels;
};
